import type { SearchV1TypesenseSearchListParams, ShiptimeCarrierLabel } from '@shipium-corp/shipium-ts-types';
import type { TypesenseSearchParams } from '~/components/shipment/SearchAndFilterShipmentsTypesense';
import type { TypesenseHit, TypesenseHighlight } from '~/types/typesense-search';
import type {
  TypesenseShipmentSearchRecord,
  SearchHighlight,
  TypesenseSearchResultWithHighlights,
} from '~/types/typesense-highlights';
import { getFieldDisplayName } from './typesense-field-labels';
import { DEFAULT_VALUES, FULFILLMENT_TYPES, LABEL_STATUSES, TYPESENSE_FIELDS } from './typesense-constants';
import { extractHazmatCategory } from './hazmat-classification-utils';
import { isValid } from 'date-fns';
import { safeDateToISOString } from '~/components/utils/dateUtils';

/**
 * Typesense search parameters without the fields that are not used by our API
 */
export type TypesenseQueryParams = Omit<SearchV1TypesenseSearchListParams, 'collection' | 'partnerSlug'> & {
  ts?: number;
  enable_typos_for_alpha_numerical_tokens?: boolean;
  infix?: string;
  num_typos?: number;
  typo_tokens_threshold?: number;
};

/**
 * Converts UI search parameters to Typesense query parameters
 */
export const adaptUIParamsToTypesense = (uiParams: TypesenseSearchParams): TypesenseQueryParams => {
  const filters: string[] = [];

  // Add date range filter if dates are provided and valid
  if (uiParams.startDate && uiParams.endDate) {
    const startDate = new Date(uiParams.startDate);
    const endDate = new Date(uiParams.endDate);

    if (isValid(startDate) && isValid(endDate)) {
      const startTimestamp = startDate.getTime();
      const endTimestamp = endDate.getTime();
      filters.push(`${TYPESENSE_FIELDS.CREATED_DATE_TIME}:[${startTimestamp}..${endTimestamp}]`);
    }
  }

  // Note: Origin, tenant, and label generation filters have been removed from the legacy filters
  // They will be added to advanced filters in a future phase

  // Handle test_mode_facet filter
  if (uiParams.facetFilters?.test_mode_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.TEST_MODE_FACET}:=${uiParams.facetFilters.test_mode_facet}`);
  }

  // Handle destination_country_code_facet filter
  if (uiParams.facetFilters?.destination_country_code_facet !== undefined) {
    filters.push(
      `${TYPESENSE_FIELDS.DESTINATION_COUNTRY_CODE_FACET}:=${uiParams.facetFilters.destination_country_code_facet}`,
    );
  }
  // Handle destination_state_facet filter
  if (uiParams.facetFilters?.destination_state_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.DESTINATION_STATE_FACET}:=${uiParams.facetFilters.destination_state_facet}`);
  }
  // Handle destination_postal_code filter with exact and prefix match
  if (uiParams.facetFilters?.destination_postal_code !== undefined) {
    const postalCode = uiParams.facetFilters.destination_postal_code;
    filters.push(`destination_postal_code:[${postalCode},${postalCode}*]`);
  }
  // Handle label_status_facet filter
  if (uiParams.facetFilters?.label_status_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.LABEL_STATUS_FACET}:=${uiParams.facetFilters.label_status_facet}`);
  }
  // Handle shipment_properties_facet filter
  if (uiParams.facetFilters?.shipment_properties_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.SHIPMENT_PROPERTIES_FACET}:=${uiParams.facetFilters.shipment_properties_facet}`);
  }
  // Handle shipment_classifications_facet filter
  if (uiParams.facetFilters?.shipment_classifications_facet !== undefined) {
    filters.push(
      `${TYPESENSE_FIELDS.SHIPMENT_CLASSIFICATIONS_FACET}:=${uiParams.facetFilters.shipment_classifications_facet}`,
    );
  }

  // Handle ship_option_facet filter
  if (uiParams.facetFilters?.ship_option_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.SHIP_OPTION_FACET}:=${uiParams.facetFilters.ship_option_facet}`);
  }

  // Handle carrier_service_method_id_facet filter
  if (uiParams.facetFilters?.carrier_service_method_id_facet !== undefined) {
    filters.push(
      `${TYPESENSE_FIELDS.CARRIER_SERVICE_METHOD_ID_FACET}:=${uiParams.facetFilters.carrier_service_method_id_facet}`,
    );
  }
  // Handle carrier_id_facet filter
  if (uiParams.facetFilters?.carrier_id_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.CARRIER_ID_FACET}:=${uiParams.facetFilters.carrier_id_facet}`);
  }
  if (uiParams.facetFilters?.shipment_type_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.SHIPMENT_TYPE_FACET}:=${uiParams.facetFilters.shipment_type_facet}`);
  }

  // Handle tenant_id_facet filter
  if (uiParams.facetFilters?.tenant_id_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.TENANT_ID_FACET}:=${uiParams.facetFilters.tenant_id_facet}`);
  }

  // Handle origin_id_facet filter
  if (uiParams.facetFilters?.origin_id_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.ORIGIN_ID_FACET}:=${uiParams.facetFilters.origin_id_facet}`);
  }

  // Handle fulfillment_context_id_facet filter
  if (uiParams.facetFilters?.fulfillment_context_id_facet !== undefined) {
    filters.push(
      `${TYPESENSE_FIELDS.FULFILLMENT_CONTEXT_ID_FACET}:=${uiParams.facetFilters.fulfillment_context_id_facet}`,
    );
  }

  return {
    q: uiParams.searchString || DEFAULT_VALUES.EMPTY_STRING,
    filter_by: filters.length > 0 ? filters.join(DEFAULT_VALUES.FILTER_OPERATOR) : undefined,
    page: uiParams.page || DEFAULT_VALUES.DEFAULT_PAGE,
    query_by: DEFAULT_VALUES.QUERY_ALL,
    per_page: uiParams.pageSize || DEFAULT_VALUES.PAGE_SIZE,
    sort_by: uiParams.sortBy
      ? `${uiParams.sortBy}:${uiParams.sortDirection || DEFAULT_VALUES.SORT_DIRECTION_DESC}`
      : undefined,
    ts: uiParams.ts,
    enable_typos_for_alpha_numerical_tokens: false,
    num_typos: 0,
    typo_tokens_threshold: 0,
  };
};

/**
 * Converts base filters to Typesense query parameters for facet fetching
 */
export const adaptUIParamsToTypesenseForFacets = (baseFilters: {
  startDate: string;
  endDate: string;
  facetFilters?: {
    test_mode_facet?: string;
    destination_country_code_facet?: string;
    destination_state_facet?: string;
    destination_postal_code?: string;
    label_status_facet?: string;
    shipment_properties_facet?: string;
    shipment_classifications_facet?: string;
    ship_option_facet?: string;
    carrier_service_method_id_facet?: string;
    carrier_id_facet?: string;
    shipment_type_facet?: string;
    tenant_id_facet?: string;
    origin_id_facet?: string;
    fulfillment_context_id_facet?: string;
  };
}): TypesenseQueryParams => {
  const filters: string[] = [];

  // Add date range filter if dates are provided and valid
  if (baseFilters.startDate && baseFilters.endDate) {
    const startDate = new Date(baseFilters.startDate);
    const endDate = new Date(baseFilters.endDate);

    if (isValid(startDate) && isValid(endDate)) {
      const startTimestamp = startDate.getTime();
      const endTimestamp = endDate.getTime();
      filters.push(`${TYPESENSE_FIELDS.CREATED_DATE_TIME}:[${startTimestamp}..${endTimestamp}]`);
    }
  }

  // Note: Origin, tenant, and label generation filters have been removed
  // They will be added as advanced filters in a future phase

  // Handle applied facet filters to get accurate counts
  if (baseFilters.facetFilters?.test_mode_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.TEST_MODE_FACET}:=${baseFilters.facetFilters.test_mode_facet}`);
  }

  if (baseFilters.facetFilters?.destination_country_code_facet !== undefined) {
    filters.push(
      `${TYPESENSE_FIELDS.DESTINATION_COUNTRY_CODE_FACET}:=${baseFilters.facetFilters.destination_country_code_facet}`,
    );
  }
  if (baseFilters.facetFilters?.destination_state_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.DESTINATION_STATE_FACET}:=${baseFilters.facetFilters.destination_state_facet}`);
  }
  if (baseFilters.facetFilters?.destination_postal_code !== undefined) {
    const postalCode = baseFilters.facetFilters.destination_postal_code;
    filters.push(`destination_postal_code:[${postalCode},${postalCode}*]`);
  }
  if (baseFilters.facetFilters?.label_status_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.LABEL_STATUS_FACET}:=${baseFilters.facetFilters.label_status_facet}`);
  }
  if (baseFilters.facetFilters?.shipment_properties_facet !== undefined) {
    filters.push(
      `${TYPESENSE_FIELDS.SHIPMENT_PROPERTIES_FACET}:=${baseFilters.facetFilters.shipment_properties_facet}`,
    );
  }
  if (baseFilters.facetFilters?.shipment_classifications_facet !== undefined) {
    filters.push(
      `${TYPESENSE_FIELDS.SHIPMENT_CLASSIFICATIONS_FACET}:=${baseFilters.facetFilters.shipment_classifications_facet}`,
    );
  }

  if (baseFilters.facetFilters?.ship_option_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.SHIP_OPTION_FACET}:=${baseFilters.facetFilters.ship_option_facet}`);
  }

  if (baseFilters.facetFilters?.carrier_service_method_id_facet !== undefined) {
    filters.push(
      `${TYPESENSE_FIELDS.CARRIER_SERVICE_METHOD_ID_FACET}:=${baseFilters.facetFilters.carrier_service_method_id_facet}`,
    );
  }
  if (baseFilters.facetFilters?.carrier_id_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.CARRIER_ID_FACET}:=${baseFilters.facetFilters.carrier_id_facet}`);
  }
  if (baseFilters.facetFilters?.shipment_type_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.SHIPMENT_TYPE_FACET}:=${baseFilters.facetFilters.shipment_type_facet}`);
  }
  if (baseFilters.facetFilters?.tenant_id_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.TENANT_ID_FACET}:=${baseFilters.facetFilters.tenant_id_facet}`);
  }
  if (baseFilters.facetFilters?.origin_id_facet !== undefined) {
    filters.push(`${TYPESENSE_FIELDS.ORIGIN_ID_FACET}:=${baseFilters.facetFilters.origin_id_facet}`);
  }
  if (baseFilters.facetFilters?.fulfillment_context_id_facet !== undefined) {
    filters.push(
      `${TYPESENSE_FIELDS.FULFILLMENT_CONTEXT_ID_FACET}:=${baseFilters.facetFilters.fulfillment_context_id_facet}`,
    );
  }

  return {
    q: DEFAULT_VALUES.EMPTY_STRING,
    filter_by: filters.length > 0 ? filters.join(DEFAULT_VALUES.FILTER_OPERATOR) : undefined,
    page: DEFAULT_VALUES.DEFAULT_PAGE,
    query_by: DEFAULT_VALUES.QUERY_ALL,
    per_page: 0, // Don't need documents for facet fetch
    enable_typos_for_alpha_numerical_tokens: false,
    num_typos: 0,
    typo_tokens_threshold: 0,
  };
};

/**
 * Helper function to extract snippet value from highlight item
 */
const extractSnippetValue = (highlightItem: TypesenseHighlight): string | null => {
  // Handle multiple snippets (array) - join with commas
  if (highlightItem.snippets && Array.isArray(highlightItem.snippets) && highlightItem.snippets.length > 0) {
    return highlightItem.snippets.filter(Boolean).join(DEFAULT_VALUES.SEPARATOR);
  }

  // Handle single snippet (string)
  if (highlightItem.snippet && typeof highlightItem.snippet === 'string') {
    return highlightItem.snippet;
  }

  return null;
};

/**
 * Processes highlight data from a Typesense hit into SearchHighlight objects
 * Uses the hit.highlights array which contains field, snippet(s), and matched_tokens
 */
export const processHighlights = (hit: TypesenseHit): SearchHighlight[] => {
  // Use the highlights array if available (standard Typesense format)
  if (!hit.highlights || !Array.isArray(hit.highlights) || hit.highlights.length === 0) {
    return [];
  }

  const result: SearchHighlight[] = [];

  hit.highlights.forEach((highlightItem) => {
    // Skip items without field names
    if (!highlightItem.field) return;

    const snippetValue = extractSnippetValue(highlightItem);
    if (!snippetValue?.trim()) return;

    result.push({
      fieldName: highlightItem.field,
      displayName: getFieldDisplayName(highlightItem.field),
      highlightedValue: snippetValue.trim(),
    });
  });

  return result;
};

export const adaptTypesenseToShipmentRecord = (
  hit: TypesenseHit,
  serviceMethodMapping: Record<string, string> = {},
): TypesenseShipmentSearchRecord => {
  const doc = hit.document;

  const serviceMethodName =
    serviceMethodMapping[doc.carrier_service_method_id_facet] || doc.carrier_service_method_id_facet;

  const desiredDeliveryDate = safeDateToISOString(doc.desired_delivery_date_time) ?? DEFAULT_VALUES.EMPTY_STRING;
  return {
    shipmentType: doc.shipment_type_facet,
    failureReason: doc._failure_reason,
    httpStatusCode: doc.http_status_code_facet,
    originState: doc.origin_state_facet || DEFAULT_VALUES.EMPTY_STRING,
    originCountryCode: doc.origin_country_code_facet || DEFAULT_VALUES.EMPTY_STRING,
    originName: doc._origin_name || DEFAULT_VALUES.EMPTY_STRING,
    destinationState: doc.destination_state_facet || DEFAULT_VALUES.EMPTY_STRING,
    shipOption: doc.ship_option_facet || DEFAULT_VALUES.EMPTY_STRING,
    desiredDeliveryDate: desiredDeliveryDate,
    referenceIdentifiers: doc.partner_reference_identifier || [],
    fulfillmentContext: doc._fulfillment_context_name,
    fulfillmentContextId: doc.fulfillment_context_id_facet ?? doc.fulfillment_context_id,
    shipiumShipmentId: doc.shipium_shipment_id,
    partnerShipmentId: doc.partner_shipment_id || DEFAULT_VALUES.EMPTY_STRING,
    carrier: doc.carrier_id_facet,
    carrierServiceName: serviceMethodName,
    serviceMethodName: doc._service_method_name || serviceMethodName,
    shipmentTags: doc.shipment_tags?.join(DEFAULT_VALUES.SEPARATOR) || DEFAULT_VALUES.EMPTY_STRING,
    destinationPostalCode: doc.destination_postal_code || DEFAULT_VALUES.EMPTY_STRING,
    asOfTimestamp: safeDateToISOString(doc.created_date_time) ?? DEFAULT_VALUES.EMPTY_STRING,
    billableWeight: doc._billable_weight?.toString() || DEFAULT_VALUES.EMPTY_STRING,
    carrierLabelCurrencyCode: doc.currency_code || DEFAULT_VALUES.CURRENCY_CODE,
    carrierLabelPrice: doc.carrier_label_price || 0,
    carrierSelectionId: doc.carrier_selection_id || DEFAULT_VALUES.EMPTY_STRING,
    carrierShipmentId: doc.carrier_shipment_ids?.[0] || DEFAULT_VALUES.EMPTY_STRING,
    createdAtTimestamp: safeDateToISOString(doc.created_date_time) ?? DEFAULT_VALUES.EMPTY_STRING,
    carrierTrackingId: doc.carrier_tracking_ids?.[0] || DEFAULT_VALUES.EMPTY_STRING,
    currencyCode: doc.currency_code || DEFAULT_VALUES.CURRENCY_CODE,
    destinationAddressType: doc._customer_address_type || DEFAULT_VALUES.EMPTY_STRING,
    destinationCountryCode: doc.destination_country_code_facet || DEFAULT_VALUES.EMPTY_STRING,
    dimWeight: 0, // Not available in basic Typesense response
    duty: 0, // Not available in basic Typesense response
    fcId: doc.origin_id_facet,
    fcSlaProcessingDays: 0, // Not available in basic Typesense response
    fromPostalCode: doc.origin_postal_code || DEFAULT_VALUES.EMPTY_STRING,
    insuranceCost: 0, // Not available in basic Typesense response
    isCustomerReturn: doc._fulfillment_type === FULFILLMENT_TYPES.RETURNS,
    isMultiParcel: (doc.multi_parcel_package_count || 0) > 1,
    fulfillmentType: mapFulfillmentType(doc._fulfillment_type),
    labelCreateParamsCustomLabelEntries: doc._custom_label_entries || {},
    labelCreateSuccess: doc.label_status_facet === LABEL_STATUSES.SUCCESS,
    labelFailoverOccurred: doc._label_failover_occurred || false,
    labelFormat: DEFAULT_VALUES.EMPTY_STRING, // Not available in basic Typesense response
    labelOrientation: DEFAULT_VALUES.EMPTY_STRING, // Not available in basic Typesense response
    labelResolution: DEFAULT_VALUES.EMPTY_STRING, // Not available in basic Typesense response
    labelSize: DEFAULT_VALUES.EMPTY_STRING, // Not available in basic Typesense response
    labelStatus: doc.label_status_facet || DEFAULT_VALUES.EMPTY_STRING,
    status: (doc.label_status_facet || DEFAULT_VALUES.EMPTY_STRING) as ShiptimeCarrierLabel['status'],
    carrierSelectionServiceMethodName: serviceMethodName,
    statusDetail: doc._carrier_selection_status_detail || DEFAULT_VALUES.EMPTY_STRING,
    labelStatusDetail: doc._carrier_label_status_detail || DEFAULT_VALUES.EMPTY_STRING,
    labelVoidSuccess: false, // Not available in basic Typesense response
    lastUpdatedAtTimestamp: safeDateToISOString(doc.created_date_time) ?? DEFAULT_VALUES.EMPTY_STRING, // Fallback to created time
    manifestSuccess: false, // Not available in basic Typesense response
    orderItemQuantities: [], // Not available in basic Typesense response
    orderToShipProcessingDays: 0, // Not available in basic Typesense response
    packageHeight: doc._height || 0,
    packageLength: doc._length || 0,
    packageLinearUnit: doc._linear_unit || DEFAULT_VALUES.LINEAR_UNIT,
    packageWeightUnit: doc._total_weight_unit || DEFAULT_VALUES.WEIGHT_UNIT,
    packageWidth: doc._width || 0,
    packagingMaterial: doc._packaging_material || DEFAULT_VALUES.EMPTY_STRING,
    partnerId: doc._partner_id,
    physicalWeight: doc.weight_pounds || 0,
    rateResultType: DEFAULT_VALUES.EMPTY_STRING, // Not available in basic Typesense response
    selectionStatus: doc.carrier_selection_status_facet || DEFAULT_VALUES.EMPTY_STRING,
    shipiumLabelId: doc.shipium_label_id || DEFAULT_VALUES.EMPTY_STRING,
    shipmentProperties: doc.shipment_properties_facet || [],
    shippingCost: doc.carrier_selection_total_cost || 0,
    taxes: 0, // Not available in basic Typesense response
    testMode: doc.test_mode_facet || false,
    totalCost: doc._operational_cost || doc.carrier_selection_total_cost || 0,
    totalWeight: doc._total_weight || doc.weight_pounds || 0,
    totalWeightUnit: doc._total_weight_unit || DEFAULT_VALUES.WEIGHT_UNIT,
    zoneId: doc.carrier_zone_id_facet || DEFAULT_VALUES.EMPTY_STRING,
    zpl: DEFAULT_VALUES.EMPTY_STRING, // Not available in basic Typesense response
    estimatedVersusActualCost: 0, // Not available in basic Typesense response
    carrierServiceMethodId: doc.carrier_service_method_id_facet,
    trackingShipmentStatus: DEFAULT_VALUES.EMPTY_STRING, // Will be populated by bulk tracking hook
    deliveredTimestamp: safeDateToISOString(doc.delivered_timestamp) ?? DEFAULT_VALUES.EMPTY_STRING,
    shippedTimestamp: safeDateToISOString(doc.shipped_date_time) ?? DEFAULT_VALUES.EMPTY_STRING,
    trackingStatusMessage: DEFAULT_VALUES.EMPTY_STRING, // Will be populated by bulk tracking hook
    referenceIdentifier: doc.partner_reference_identifier?.[0] || DEFAULT_VALUES.EMPTY_STRING,
    shipiumTenantId: doc.tenant_id_facet,
    shipiumTrackingId: doc.shipium_tracking_id,

    // Add search highlights
    searchHighlights: processHighlights(hit),

    hazmatCategory: extractHazmatCategory(doc.shipment_classifications_facet),
  };
};

/**
 * Transforms Typesense search response to UI-friendly format
 */
export const adaptTypesenseSearchResponse = (
  response: import('~/types/typesense-search').TypesenseSearchResponse,
  serviceMethodMapping: Record<string, string> = {},
): TypesenseSearchResultWithHighlights => {
  return {
    results: response.hits.map((hit) => adaptTypesenseToShipmentRecord(hit, serviceMethodMapping)),
    totalCount: response.found,
    totalDocuments: response.out_of,
    searchTime: response.search_time_ms,
    usingPartnerShipmentId: checkForPartnerShipmentIdUsage(response.hits),
    searchTerm: response.request_params?.q || DEFAULT_VALUES.EMPTY_STRING,
    facetCounts: response.facet_counts,
  };
};

/**
 * Helper function to map fulfillment type
 */
function mapFulfillmentType(
  fulfillmentType?: string,
): 'customer' | 'at_large' | 'returns' | 'hundredweight' | 'reship' | 'unknown' {
  switch (fulfillmentType) {
    case FULFILLMENT_TYPES.CUSTOMER:
    case FULFILLMENT_TYPES.AT_LARGE:
    case FULFILLMENT_TYPES.RETURNS:
    case FULFILLMENT_TYPES.HUNDREDWEIGHT:
    case FULFILLMENT_TYPES.RESHIP:
      return fulfillmentType;
    default:
      return FULFILLMENT_TYPES.UNKNOWN;
  }
}

/**
 * Helper function to determine if search results are using partner shipment ID
 */
function checkForPartnerShipmentIdUsage(hits: TypesenseHit[]): boolean {
  return hits.some((hit) => !!hit.document.partner_shipment_id);
}
